import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { CustomSelect } from "@/components/ui/custom-select";
import { getSportPositionsForSport } from "@/utils/sport-positions";
import {
  getSportFieldLayout,
  getSportPositionMarkers,
  type SportCourtStyle,
} from "@/utils/sport-position-layouts";

type SportPositionPickerProps = {
  sport?: string | null;
  sportCategory?: string | null;
  value: string;
  onChange: (value: string) => void;
  label?: string;
  size?: "sm" | "default";
  compact?: boolean;
  className?: string;
  disabled?: boolean;
};

function CourtMarkings({ style }: { style: SportCourtStyle }) {
  if (style === "football") {
    return (
      <>
        <div className="absolute inset-x-[12%] top-[8%] bottom-[8%] rounded-lg border border-white/25" />
        <div className="absolute left-1/2 top-[8%] bottom-[8%] w-px -translate-x-1/2 bg-white/20" />
        <div className="absolute left-1/2 top-1/2 h-[18%] w-[18%] -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/20" />
        <div className="absolute inset-x-[28%] bottom-[8%] h-[14%] rounded-t-lg border border-b-0 border-white/20" />
      </>
    );
  }

  if (style === "volleyball-indoor" || style === "volleyball-sand") {
    const isSand = style === "volleyball-sand";
    return (
      <>
        <div className="absolute inset-x-[10%] top-[8%] bottom-[8%] rounded-lg border border-white/25" />
        {/* Metade adversária */}
        <div className="absolute inset-x-[10%] top-[8%] bottom-1/2 rounded-t-lg bg-black/10" />
        {/* Rede */}
        <div className="absolute inset-x-[8%] top-1/2 h-1 -translate-y-1/2 rounded-full bg-white/60 shadow-sm" />
        <span className="absolute left-[11%] top-[calc(50%-0.65rem)] text-[8px] font-bold uppercase tracking-wider text-white/50">
          Rede
        </span>
        {isSand ? (
          <>
            <div className="absolute inset-x-[10%] top-[60%] border-b border-dashed border-white/15" />
            <div className="absolute inset-x-[10%] top-[76%] border-b border-dashed border-white/15" />
            <span className="absolute right-[11%] top-[63%] text-[8px] font-bold uppercase tracking-wider text-white/45">
              Ataque
            </span>
            <span className="absolute right-[11%] top-[79%] text-[8px] font-bold uppercase tracking-wider text-white/45">
              Fundo
            </span>
          </>
        ) : (
          <div className="absolute inset-x-[10%] top-[62%] border-b border-dashed border-white/25" />
        )}
      </>
    );
  }

  if (style === "beach-tennis") {
    return (
      <>
        <div className="absolute inset-[12%] rounded-lg border border-white/25" />
        <div className="absolute inset-y-[12%] left-1/2 w-px -translate-x-1/2 bg-white/25" />
        <div className="absolute inset-x-[12%] top-1/2 h-px -translate-y-1/2 bg-white/20" />
      </>
    );
  }

  return (
    <>
      <div className="absolute inset-x-[12%] top-[10%] bottom-[10%] rounded-lg border border-white/25" />
      <div className="absolute inset-x-[10%] top-1/2 h-px -translate-y-1/2 bg-white/20" />
    </>
  );
}

function courtSurfaceClass(style: SportCourtStyle): string {
  switch (style) {
    case "football":
      return "bg-gradient-to-t from-emerald-800/90 via-emerald-700/80 to-emerald-600/70";
    case "volleyball-indoor":
      return "bg-gradient-to-t from-orange-950/70 via-orange-900/60 to-orange-800/50";
    case "volleyball-sand":
      return "bg-gradient-to-t from-amber-900/60 via-amber-700/50 to-amber-500/40";
    case "beach-tennis":
      return "bg-gradient-to-br from-sky-900/50 via-cyan-800/40 to-amber-700/40";
    case "carimba":
      return "bg-gradient-to-t from-indigo-950/70 via-indigo-900/60 to-indigo-800/50";
  }
}

export function SportPositionPicker({
  sport,
  sportCategory,
  value,
  onChange,
  label = "Posição pretendida",
  size = "sm",
  compact = false,
  className,
  disabled,
}: SportPositionPickerProps) {
  const positions = getSportPositionsForSport(sport, sportCategory);
  const layout = getSportFieldLayout(sport, sportCategory);
  const markers = getSportPositionMarkers(sport, sportCategory);

  if (positions.length === 0) return null;

  const options = [
    { value: "", label: "Aleatório / sem preferência" },
    ...positions,
  ];

  return (
    <div className={cn("flex flex-col gap-3", className)}>
      {label ? (
        <p className="text-sm font-bold text-foreground ml-1">{label}</p>
      ) : null}

      {layout && markers && markers.length > 0 ? (
        <div className={cn("flex flex-col gap-2", compact ? "max-w-[220px]" : "max-w-sm")}>
          <div
            className={cn(
              "relative w-full overflow-hidden rounded-xl border-2 border-border shadow-inner",
              courtSurfaceClass(layout.courtStyle),
            )}
            style={{ aspectRatio: layout.aspectRatio }}
          >
            <CourtMarkings style={layout.courtStyle} />
            {markers.map((marker) => {
              const isSelected = value === marker.value;
              return (
                <button
                  key={marker.value}
                  type="button"
                  disabled={disabled}
                  title={marker.label}
                  aria-label={marker.label}
                  aria-pressed={isSelected}
                  onClick={() => onChange(marker.value)}
                  className={cn(
                    "absolute -translate-x-1/2 -translate-y-1/2 rounded-full border-2 font-bold transition-all",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                    compact ? "min-w-[2.25rem] px-1.5 py-1 text-[9px]" : "min-w-[2.75rem] px-2 py-1.5 text-[10px]",
                    isSelected
                      ? "border-primary bg-primary text-primary-foreground shadow-lg scale-110 z-10"
                      : "border-white/70 bg-background/90 text-foreground hover:bg-background hover:scale-105",
                    disabled && "opacity-50 cursor-not-allowed")}
                  style={{ left: `${marker.x}%`, top: `${marker.y}%` }}
                >
                  {compact ? marker.shortLabel : marker.label}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      <CustomSelect
        value={value}
        onChange={onChange}
        placeholder="Aleatório"
        size={size}
        disabled={disabled}
        options={options}
      />

      {value ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={disabled}
          onClick={() => onChange("")}
          className="self-start h-auto px-2 py-1 text-xs text-muted-foreground hover:text-foreground"
        >
          Limpar preferência (aleatório)
        </Button>
      ) : null}
    </div>
  );
}
